import Highcharts from "highcharts";
import type { GaugeComponentSpec } from "../Processing/types";

function buildPlotBands(
  spec: Pick<GaugeComponentSpec, "segment_config">,
  minValue: number,
  maxValue: number,
): Highcharts.YAxisPlotBandsOptions[] {
  const { thresholds, colors, segments } = spec.segment_config as {
    thresholds: number[];
    colors: string[];
    segments: number;
  };
  const range = maxValue - minValue;
  const bands: Highcharts.YAxisPlotBandsOptions[] = [];

  for (let i = 0; i < segments; i++) {
    const fromPct = i === 0 ? 0 : thresholds[i - 1];
    const toPct = i === segments - 1 ? 1 : thresholds[i];
    bands.push({
      from: minValue + fromPct * range,
      to: minValue + toPct * range,
      color: colors[i],
      thickness: 20,
    });
  }
  return bands;
}

export function buildGaugeOptions(
  spec: Pick<GaugeComponentSpec, "segment_config" | "display">,
  needleValue: number | null,
  minValue: number,
  maxValue: number,
  numDecimals: number | null = null,
): Highcharts.Options {
  const plotBands = buildPlotBands(spec, minValue, maxValue);

  // The dial position is clamped to the axis max so the needle never points past
  // the end of the scale, even when the real value is higher. The data label still
  // shows the true value (the widget surfaces an overflow warning separately).
  const needleIsNull = needleValue == null;
  const dialPosition = needleIsNull ? minValue : Math.min(needleValue, maxValue);
  const displayValue = needleIsNull ? minValue : needleValue;
  const exceedsMax = !needleIsNull && needleValue > maxValue;
  // The backend reports the metric's decimal count with the gauge data, so a
  // 2-decimal metric shows 0.50. Without it the number renders as it is.
  const fmt = (n: number) => (numDecimals != null ? n.toFixed(numDecimals) : String(n));

  // Amber warning glyph shown beside the value label when the true value is above
  // the gauge maximum (the dial itself is pinned to max). Rendered in the label's
  // markup so it sits next to the number rather than in the widget header.
  const warningMark = exceedsMax
    ? ` <span style="font-size:15px;color:#faad14" title="Value exceeds the gauge maximum (${fmt(maxValue)})">⚠</span>`
    : "";

  return {
    chart: {
      type: "gauge",
      backgroundColor: "transparent",
      plotBackgroundColor: undefined,
      plotBorderWidth: 0,
      plotShadow: false,
      spacing: [8, 8, 8, 8],
    },
    title: { text: "" },
    pane: {
      startAngle: -90,
      endAngle: 90,
      center: ["50%", "75%"],
      size: "100%",
      background: [
        {
          shape: "arc",
          innerRadius: "0%",
          outerRadius: "100%",
          backgroundColor: "transparent",
          borderWidth: 0,
        },
      ],
    },
    yAxis: {
      min: minValue,
      max: maxValue,
      minorTickInterval: "auto",
      minorTickWidth: 1,
      minorTickLength: 10,
      minorTickPosition: "inside",
      minorTickColor: "#666",
      tickWidth: 2,
      tickPosition: "inside",
      tickLength: 10,
      tickColor: "#666",
      // Only tick/label the endpoints so min and max are always visible (and uncluttered at small sizes).
      tickPositions: [minValue, maxValue],
      labels: { distance: 16 },
      title: { text: "" },
      plotBands,
    },
    series: [
      {
        type: "gauge",
        name: spec.display.title || "Value",
        data: [{ y: dialPosition, custom: { realValue: fmt(displayValue) } }],
        dataLabels: {
          useHTML: true,
          format:
            `<div style="text-align:center"><span style="font-size:18px;color:${needleIsNull ? "#ccc" : "#333"}">{point.custom.realValue}</span>${warningMark}</div>`,
          borderWidth: 0,
          verticalAlign: "top",
          y: 36,
        },
        dial: { radius: "80%", backgroundColor: "#333", baseWidth: 12, baseLength: "0%", rearLength: "0%" },
        pivot: { backgroundColor: "#333", radius: 6 },
      },
    ] as Highcharts.SeriesOptionsType[],
    tooltip: { enabled: false },
    credits: { enabled: false },
  };
}
